import { FormInstance, Rule } from 'antd/es/form';
import { useEffect, useMemo, useRef, useState } from 'react';

import { RULES } from '@/constants';

type FieldPath = string | (string | number)[];

interface UseRequiredIfHadValueOptions {
  form: FormInstance;
  userId?: string;
  excludeFields?: FieldPath[];
}

interface FieldRulesMap {
  [key: string]: boolean;
}

export const useRequiredIfHadValue = ({
  form,
  userId,
  excludeFields = []
}: UseRequiredIfHadValueOptions) => {
  const initialFormValuesRef = useRef<Record<string, unknown> | null>(null);
  const lastUserIdRef = useRef<string | undefined>(undefined);
  const [requiredFieldsMap, setRequiredFieldsMap] = useState<FieldRulesMap>({});

  const serializedExcludeFields = JSON.stringify(excludeFields);
  const stableExcludeFields = useMemo<FieldPath[]>(
    () => JSON.parse(serializedExcludeFields),
    [serializedExcludeFields]
  );

  useEffect(() => {
    if (!userId) {
      setRequiredFieldsMap({});
      initialFormValuesRef.current = null;
      lastUserIdRef.current = undefined;
      return;
    }

    if (initialFormValuesRef.current === null || lastUserIdRef.current !== userId) {
      initialFormValuesRef.current = form.getFieldsValue(true);
      lastUserIdRef.current = userId;
    }

    const calculateRequiredFields = () => {
      const initialFormValues = initialFormValuesRef.current;
      if (!initialFormValues) return {};

      const requiredFieldsResult: FieldRulesMap = {};

      const fieldHasNonEmptyValue = (fieldValue: unknown): boolean => {
        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '';
      };

      const isFieldExcludedFromRequired = (fieldPath: FieldPath): boolean => {
        const fieldPathAsString = JSON.stringify(
          Array.isArray(fieldPath) ? fieldPath : [fieldPath]
        );
        return stableExcludeFields.some(excludedFieldPath => {
          const excludedPathAsString = JSON.stringify(
            Array.isArray(excludedFieldPath) ? excludedFieldPath : [excludedFieldPath]
          );
          return fieldPathAsString === excludedPathAsString;
        });
      };

      const traverseObjectAndMarkRequiredFields = (
        objectToProcess: unknown,
        parentFieldPath: (string | number)[] = []
      ) => {
        if (!objectToProcess || typeof objectToProcess !== 'object') return;

        Object.entries(objectToProcess).forEach(([fieldKey, fieldValue]) => {
          const fullFieldPath = [...parentFieldPath, fieldKey];

          if (isFieldExcludedFromRequired(fullFieldPath)) {
            return;
          }

          const isNestedObject =
            fieldValue &&
            typeof fieldValue === 'object' &&
            !Array.isArray(fieldValue) &&
            !(fieldValue instanceof Date);

          if (isNestedObject) {
            traverseObjectAndMarkRequiredFields(fieldValue, fullFieldPath);
          } else {
            const fieldPathString = fullFieldPath.join('.');
            requiredFieldsResult[fieldPathString] = fieldHasNonEmptyValue(fieldValue);
          }
        });
      };

      traverseObjectAndMarkRequiredFields(initialFormValues);
      return requiredFieldsResult;
    };

    setRequiredFieldsMap(calculateRequiredFields());
  }, [userId, form, stableExcludeFields]);

  const getFieldRules = (fieldPath: FieldPath, additionalRules: Rule[] = []): Rule[] => {
    const fieldPathString = Array.isArray(fieldPath) ? fieldPath.join('.') : fieldPath;
    const isFieldRequired = requiredFieldsMap[fieldPathString] || false;

    if (!isFieldRequired) {
      return additionalRules;
    }

    return [RULES.REQUIRED, ...additionalRules];
  };

  const requiredIfHadValueValidator = (fieldPath: FieldPath) => ({
    validator: (_: unknown, fieldValue: unknown) => {
      const fieldPathString = Array.isArray(fieldPath) ? fieldPath.join('.') : fieldPath;
      const isFieldRequired = requiredFieldsMap[fieldPathString] || false;

      if (!isFieldRequired) {
        return Promise.resolve();
      }

      const fieldIsEmpty =
        fieldValue === undefined || fieldValue === null || String(fieldValue).trim() === '';
      const errorMessage = RULES.REQUIRED.message;
      return fieldIsEmpty ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
    }
  });

  return {
    getFieldRules,
    requiredIfHadValueValidator,
    requiredFields: requiredFieldsMap,
    isFieldRequired: (fieldPath: FieldPath) => {
      const fieldPathString = Array.isArray(fieldPath) ? fieldPath.join('.') : fieldPath;
      return requiredFieldsMap[fieldPathString] || false;
    }
  };
};
